import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DrawerModule } from 'primeng/drawer';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Cliente } from '@core/models/models';
import { ClienteService } from '@core/services/domain-services';
import { FeedbackService } from '@core/services/feedback.service';
import { extractErrorMessage } from '@core/utils/error-parser.util';
import {
  CriarDocumentoRequestDto,
  DocumentLineRequestDto,
  EnumDocumentStatus,
  FeDocumento,
  FeProduto,
  FeTaxCode,
  FeTaxExemptionCode,
  FeTipoDocumento,
} from '@shared/api/models/faturacao-eletronica.model';
import { FaturacaoEletronicaService } from '@shared/api/services/faturacao-eletronica.service';

@Component({
  selector: 'app-fatura-drawer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DrawerModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    ButtonModule,
    CheckboxModule,
    TooltipModule,
  ],
  templateUrl: './fatura-drawer.component.html',
  styleUrl: './fatura-drawer.component.scss',
})
export class FaturaDrawerComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly faturacaoService = inject(FaturacaoEletronicaService);
  private readonly clienteService = inject(ClienteService);
  private readonly feedback = inject(FeedbackService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() modo: 'criar' | 'retificar' = 'criar';
  @Input() documentoOriginal: FeDocumento | null = null;
  @Output() saved = new EventEmitter<FeDocumento>();

  clientes: Cliente[] = [];
  tiposDocumento: FeTipoDocumento[] = [];
  produtos: FeProduto[] = [];
  taxCodes: FeTaxCode[] = [];
  isencoes: FeTaxExemptionCode[] = [];

  loadingAux = false;
  saving = false;
  submitting = false;

  form!: FormGroup;

  ngOnInit(): void {
    this.initForm();
    this.carregarAuxiliares();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      if (this.modo === 'retificar' && this.documentoOriginal) {
        this.preencherParaRetificacao(this.documentoOriginal);
      } else if (this.modo === 'criar') {
        this.resetarForm();
      }
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      idCliente: [null, Validators.required],
      postoDesignacao: [''],
      clienteEmail: [''],
      clienteNif: [''],
      idTipoDocumento: [null, Validators.required],
      enviarAposValidacao: [true],
      lines: this.fb.array([]),
    });

    if (this.lines.length === 0) {
      this.adicionarLinha();
    }

    // Monitor client changes to auto-fill NIF and Email
    this.form.get('idCliente')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((clienteId: number | null) => {
        if (!clienteId) return;
        const cliente = this.clientes.find((c) => c.id === clienteId);
        if (cliente) {
          this.form.patchValue(
            {
              clienteNif: cliente.nif || '',
              clienteEmail: cliente.email || '',
            },
            { emitEvent: false },
          );
        }
      });
  }

  get lines(): FormArray {
    return this.form.get('lines') as FormArray;
  }

  carregarAuxiliares(): void {
    this.loadingAux = true;
    forkJoin({
      clientes: this.clienteService.getAll({ pageSize: 100 }).pipe(
        map((res) => res.data || []),
        catchError(() => of([])),
      ),
      tipos: this.faturacaoService.listarTiposDocumento().pipe(catchError(() => of([]))),
      produtos: this.faturacaoService.listarProdutos().pipe(catchError(() => of([]))),
      taxCodes: this.faturacaoService.listarTaxCodes().pipe(catchError(() => of([]))),
      isencoes: this.faturacaoService.listarIsencoes().pipe(catchError(() => of([]))),
    })
      .pipe(
        finalize(() => (this.loadingAux = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.clientes = res.clientes;
          this.tiposDocumento = res.tipos;
          this.produtos = res.produtos;
          this.taxCodes = res.taxCodes;
          this.isencoes = res.isencoes;

          // Default tipo to FT (Fatura) or FR (Fatura-Recibo)
          if (!this.form.get('idTipoDocumento')?.value && this.tiposDocumento.length > 0) {
            const ft = this.tiposDocumento.find((t) => t.codigo === 'FT') ?? this.tiposDocumento[0];
            this.form.patchValue({ idTipoDocumento: ft.id });
          }

          // If default lines don't have tax code, assign 14% (NOR)
          const defaultTax = this.taxCodes.find((t) => t.taxPercentage === 14) ?? this.taxCodes[0];
          this.lines.controls.forEach((control) => {
            if (!control.get('idTaxCode')?.value && defaultTax) {
              control.patchValue({ idTaxCode: defaultTax.id });
            }
          });
        },
      });
  }

  resetarForm(): void {
    if (!this.form) return;
    const defaultTipo = this.tiposDocumento.find((t) => t.codigo === 'FT')?.id ?? this.tiposDocumento[0]?.id ?? null;
    this.form.reset({
      idCliente: null,
      postoDesignacao: '',
      clienteEmail: '',
      clienteNif: '',
      idTipoDocumento: defaultTipo,
      enviarAposValidacao: true,
    });
    this.lines.clear();
    this.adicionarLinha();
  }

  preencherParaRetificacao(doc: FeDocumento): void {
    const defaultTax = this.taxCodes.find((t) => t.taxPercentage === 14) ?? this.taxCodes[0];
    this.form.patchValue({
      idCliente: doc.idCliente ?? doc.cliente?.id ?? null,
      clienteNif: doc.cliente?.nif ?? '',
      clienteEmail: doc.cliente?.email ?? '',
      idTipoDocumento: doc.idTipoDocumento ?? null,
      enviarAposValidacao: true,
    });

    this.lines.clear();
    const docLinhas = doc.lines || [];
    if (docLinhas.length > 0) {
      docLinhas.forEach((linha) => {
        this.adicionarLinha({
          idProduct: linha.idProduct,
          quantity: linha.quantity ?? 1,
          unitPrice: linha.unitPrice ?? 0,
          idTaxCode: linha.idTaxCode ?? defaultTax?.id,
          precoComImpostoIncluido: linha.precoComImpostoIncluido ?? true,
          idTaxExemptionCode: linha.idTaxExemptionCode ?? null,
        });
      });
    } else {
      this.adicionarLinha();
    }
  }

  adicionarLinha(initData?: {
    idProduct?: number | null;
    quantity?: number;
    unitPrice?: number;
    idTaxCode?: number | null;
    precoComImpostoIncluido?: boolean;
    idTaxExemptionCode?: number | null;
  }): void {
    const defaultTax = this.taxCodes.find((t) => t.taxPercentage === 14) ?? this.taxCodes[0];
    const group = this.fb.group({
      idProduct: [initData?.idProduct ?? null, Validators.required],
      quantity: [initData?.quantity ?? 1, [Validators.required, Validators.min(0.01)]],
      unitPrice: [initData?.unitPrice ?? 0, [Validators.required, Validators.min(0)]],
      idTaxCode: [initData?.idTaxCode ?? defaultTax?.id ?? null, Validators.required],
      precoComImpostoIncluido: [initData?.precoComImpostoIncluido ?? true],
      idTaxExemptionCode: [initData?.idTaxExemptionCode ?? null],
    });

    this.lines.push(group);
  }

  removerLinha(index: number): void {
    if (this.lines.length > 1) {
      this.lines.removeAt(index);
    }
  }

  onProdutoSelecionado(index: number, produtoId: number): void {
    const prod = this.produtos.find((p) => p.id === produtoId);
    if (!prod) return;
    const preco = prod.unitPrice ?? prod.preco ?? 0;
    const line = this.lines.at(index);
    line.patchValue({ unitPrice: preco });
  }

  getTaxPercentage(idTaxCode: number | null): number {
    if (!idTaxCode) return 0;
    const tax = this.taxCodes.find((t) => t.id === Number(idTaxCode));
    return tax ? Number(tax.taxPercentage) : 0;
  }

  calcularSubtotalLinha(index: number): number {
    const line = this.lines.at(index);
    const qtd = Number(line.get('quantity')?.value) || 0;
    const preco = Number(line.get('unitPrice')?.value) || 0;
    return qtd * preco;
  }

  get subtotalGeral(): number {
    let sub = 0;
    for (let i = 0; i < this.lines.length; i++) {
      const line = this.lines.at(i);
      const qtd = Number(line.get('quantity')?.value) || 0;
      const preco = Number(line.get('unitPrice')?.value) || 0;
      const idTax = line.get('idTaxCode')?.value;
      const taxa = this.getTaxPercentage(idTax);
      const precoComIva = line.get('precoComImpostoIncluido')?.value;
      const base = qtd * preco;
      if (precoComIva && taxa > 0) {
        sub += base / (1 + taxa / 100);
      } else {
        sub += base;
      }
    }
    return sub;
  }

  get impostoGeral(): number {
    let imp = 0;
    for (let i = 0; i < this.lines.length; i++) {
      const line = this.lines.at(i);
      const qtd = Number(line.get('quantity')?.value) || 0;
      const preco = Number(line.get('unitPrice')?.value) || 0;
      const idTax = line.get('idTaxCode')?.value;
      const taxa = this.getTaxPercentage(idTax);
      const precoComIva = line.get('precoComImpostoIncluido')?.value;
      const base = qtd * preco;
      if (taxa > 0) {
        if (precoComIva) {
          const liquido = base / (1 + taxa / 100);
          imp += base - liquido;
        } else {
          imp += (base * taxa) / 100;
        }
      }
    }
    return imp;
  }

  get totalGeral(): number {
    return this.subtotalGeral + this.impostoGeral;
  }

  get nifValido(): boolean {
    const nif = this.form.get('clienteNif')?.value;
    return typeof nif === 'string' && nif.trim().length >= 9;
  }

  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  salvar(submeterAposSalvar = false): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.feedback.showWarning('Preencha os campos obrigatórios antes de continuar.');
      return;
    }

    const payloadLines: DocumentLineRequestDto[] = this.lines.controls.map((ctrl) => {
      const idTaxCode = Number(ctrl.get('idTaxCode')?.value);
      const taxa = this.getTaxPercentage(idTaxCode);
      const idTaxExemption = taxa === 0 ? ctrl.get('idTaxExemptionCode')?.value : null;

      return {
        idProduct: Number(ctrl.get('idProduct')?.value),
        quantity: Number(ctrl.get('quantity')?.value),
        unitPrice: Number(ctrl.get('unitPrice')?.value),
        idTaxCode,
        precoComImpostoIncluido: Boolean(ctrl.get('precoComImpostoIncluido')?.value),
        idTaxExemptionCode: idTaxExemption ? Number(idTaxExemption) : undefined,
      };
    });

    const payload: CriarDocumentoRequestDto = {
      idTipoDocumento: Number(this.form.get('idTipoDocumento')?.value),
      idCliente: Number(this.form.get('idCliente')?.value),
      seriesContingencyIndicator: false,
      lines: payloadLines,
    };

    if (submeterAposSalvar) {
      this.submitting = true;
    } else {
      this.saving = true;
    }

    const request$ =
      this.modo === 'retificar' && this.documentoOriginal
        ? this.faturacaoService.corrigirDocumento(this.documentoOriginal.id, payload)
        : this.faturacaoService.criarDocumento(payload);

    request$
      .pipe(
        finalize(() => {
          this.saving = false;
          if (!submeterAposSalvar) {
            this.submitting = false;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (docCriado: FeDocumento) => {
          if (submeterAposSalvar) {
            this.faturacaoService
              .submeterDocumento(docCriado.id)
              .pipe(
                finalize(() => (this.submitting = false)),
                takeUntilDestroyed(this.destroyRef),
              )
              .subscribe({
                next: (docSubmetido: FeDocumento) => {
                  this.feedback.showSuccess('Fatura emitida e submetida à AGT com sucesso!');
                  this.saved.emit(docSubmetido);
                  this.fechar();
                },
                error: (err: unknown) => {
                  this.feedback.showError(
                    `Fatura gravada (ID: ${docCriado.id}), mas falhou o envio à AGT: ${extractErrorMessage(err)}`,
                  );
                  this.saved.emit(docCriado);
                  this.fechar();
                },
              });
          } else {
            this.feedback.showSuccess('Rascunho de fatura guardado com sucesso.');
            this.saved.emit(docCriado);
            this.fechar();
          }
        },
        error: (err: unknown) => {
          this.feedback.showError(`Erro ao guardar documento: ${extractErrorMessage(err)}`);
        },
      });
  }
}
